import { Box, SxProps } from "@mui/material";
import { useEffect, useState } from "react";
import { GroupedTableGroup } from "./GroupedTableGroup";

import { ItemGroup } from "../../../../features/invoices/invoiceAPI";
import { TablePaginationProps } from "../pagination/types";
import { GroupedTableSearchProps } from "./search/useGroupedTableSearch";

export enum GroupedTableCellWidth {
  NARROW = "narrow",
  NORMAL = "normal",
  WIDE = "wide",
}

export interface GroupedTableColumn {
  fieldName: string;
  displayName: string;
  width: string;
  maxWidth?: string;
  flairComponent?: boolean;
  sx?: SxProps;
  headerSx?: SxProps;
  disableTooltip?: boolean;
  align?: "left" | "right" | "center";
  isTimeline?: boolean;
}

export interface GroupedTableLink {
  index: number;
  text: string;
  to: string;
  state?: any;
}

interface Props<T> {
  columns: GroupedTableColumn[];
  groups: ItemGroup<T>[];
  itemCount: number;
  pagination?: TablePaginationProps;
  translation: string;
  updateSelectedItems?: (items: any[]) => void;
  onClickRow?: (row: any) => void;
  customSelectHandler?: (
    item: any,
    selected: boolean,
    selectedItems: any,
  ) => any[];
  hideAllCheckboxes?: boolean;
  disableSelectAllItems?: boolean;
  disableSelectGroupItems?: boolean;
  disableSelectItems?: boolean;
  allowBorders?: boolean;
  tableHeaderBackgroundColor?: string;
  search?: GroupedTableSearchProps;
  clearSelectedItems?: boolean;
  buildUrl?: (item: T) => string;
}

export const GroupedTable = <T,>({
  columns,
  groups,
  itemCount,
  pagination,
  translation,
  updateSelectedItems,
  onClickRow,
  customSelectHandler,
  hideAllCheckboxes,
  disableSelectAllItems = false,
  disableSelectGroupItems = false,
  disableSelectItems = false,
  allowBorders = false,
  tableHeaderBackgroundColor,
  search,
  clearSelectedItems,
  buildUrl,
}: Props<T>) => {
  const emptyGroup = { items: [], name: "" };
  const [selectedItems, setSelectedItems] = useState<any[]>([]);

  useEffect(() => {
    if (clearSelectedItems) {
      setSelectedItems([]);
    }
  }, [clearSelectedItems]);

  const selectAll = (checkBoxSelected: boolean) => {
    if (checkBoxSelected) {
      let allItems: any = [];
      // Items array items can be objects or array of objects, so we need to flatten the array
      groups.map((group) => allItems.push(...group.items.flat()));
      setSelectedItems(allItems);
    } else {
      setSelectedItems([]);
    }
  };

  const selectOneGroup = (group: ItemGroup<T>, checkBoxSelected: boolean) => {
    if (checkBoxSelected) {
      // Add group items to selected items if they're not already there
      const newSelectedItems = [
        ...selectedItems,
        ...group.items.filter(
          (item) =>
            !selectedItems.some(
              (selectedItem) => selectedItem.id === (item as any).id,
            ),
        ),
      ];
      setSelectedItems(newSelectedItems);
    } else {
      // Remove group items from selected items
      const newSelectedItems = selectedItems.filter(
        (selectedItem) =>
          !group.items.some((item) => selectedItem.id === (item as any).id),
      );
      setSelectedItems(newSelectedItems);
    }
  };

  const selectOne = (item: any, checkBoxSelected: boolean) => {
    if (!!customSelectHandler) {
      // Optional custom behaviour
      const items = customSelectHandler(item, checkBoxSelected, selectedItems);
      setSelectedItems(items);
    } else {
      // Default behaviour
      if (checkBoxSelected) {
        setSelectedItems([...selectedItems, item]);
      } else {
        setSelectedItems(
          selectedItems.filter((selectedItem) => selectedItem.id !== item.id),
        );
      }
    }
  };

  useEffect(() => {
    if (disableSelectItems || !updateSelectedItems) return;
    updateSelectedItems(selectedItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems]);

  return (
    <Box>
      {groups.length > 0 ? (
        groups.map((category, i) => (
          <GroupedTableGroup
            index={i}
            columns={columns}
            key={i}
            category={category}
            isFirstCategory={i === 0}
            itemCount={itemCount}
            pagination={pagination}
            translation={translation}
            selectItem={selectOne}
            selectAll={selectAll}
            selectGroup={selectOneGroup}
            selectedItems={selectedItems}
            onClickRow={onClickRow}
            hideAllCheckboxes={hideAllCheckboxes}
            disableSelectAllItems={disableSelectAllItems}
            disableSelectGroupItems={disableSelectGroupItems}
            disableSelectItems={disableSelectItems}
            allowBorders={allowBorders}
            tableHeaderBackgroundColor={tableHeaderBackgroundColor}
            search={search}
            buildUrl={buildUrl}
          />
        ))
      ) : (
        <GroupedTableGroup
          columns={columns}
          category={emptyGroup}
          isFirstCategory={true}
          itemCount={itemCount}
          pagination={pagination}
          translation={translation}
          selectItem={() => {}}
          selectAll={() => {}}
          selectGroup={() => {}}
          selectedItems={[]}
          onClickRow={() => {}}
          hideAllCheckboxes={hideAllCheckboxes}
          disableSelectAllItems={disableSelectAllItems}
          disableSelectGroupItems={disableSelectGroupItems}
          disableSelectItems={disableSelectItems}
          allowBorders={allowBorders}
          tableHeaderBackgroundColor={tableHeaderBackgroundColor}
          buildUrl={buildUrl}
        />
      )}
    </Box>
  );
};
